import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Button, Card, Col, Descriptions, Modal, Row, Select, Table, Tag} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {useHistory, useLocation, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {deleteCompany, getCompany, setCompany, setCompanySideFilter, setWSCompany} from "../../store/companyReducer"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {getObjectGroups, setObjectGroups, setWSObjectGroup} from "../../store/objectGroupReducer"
import {center} from "../../helps/tableHelper"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {FormSelect} from "../../components/Form/FormSelect"
import {getObject} from "../../store/objectReducer"
import {FormInput} from "../../components/Form/FormInput"

const columns = [
    {
        title: 'Название',
        dataIndex: 'name',
        key: 'name',
        align: center,
    },
    {
        title: 'Статус',
        dataIndex: 'active',
        key: 'active',
        render: (active: boolean) => {
            return (
                <Tag color={active ? 'green' : 'volcano'} style={{marginRight: 0}}>{active ? 'Активно' : 'Не активно'}</Tag>
            )
        },
        align: center,
    },
]

export const Company = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const company = useSelector((state: StateType) => state.companyReducer.company)
    const filter = useSelector((state: StateType) => state.companyReducer.sideFilter)
    const objectGroups = useSelector((state: StateType) => state.objectGroupReducer.objectGroups)
    const ws = useSelector((state: StateType) => state.companyReducer.ws)
    const wsObjectGroup = useSelector((state: StateType) => state.objectGroupReducer.ws)
    const [showModal, setShowModal] = useState(false)
    const controller = useMemo(() => new AbortController(), [id])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setCompanySideFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    useEffect(() => {
        if (id) dispatch(getCompany({id, controller}))

        return () => {
            controller.abort()
            dispatch(setCompany(null))
        }
    }, [dispatch, id, controller])

    const getObjectGroupsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (id && location?.search) dispatch(getObjectGroups({idCompany: id, ...filterFromString(location.search), controller, withoutLoadingIndicator}))
    }, [dispatch, id, location, controller])

    useEffect(() => {
        getObjectGroupsFunc()

        return () => {
            dispatch(setObjectGroups(null))
        }
    }, [dispatch, getObjectGroupsFunc])

    useEffect(() => {
        if (ws && ws.id === id) {
            dispatch(getCompany({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSCompany(null))
    }, [dispatch, ws, id, controller])

    useEffect(() => {
        if (wsObjectGroup && wsObjectGroup.idParent === id) {
            getObjectGroupsFunc(true)
        }
        dispatch(setWSObjectGroup(null))
    }, [dispatch, id, wsObjectGroup, getObjectGroupsFunc])

    const deleteHandler = useCallback(async () =>
    {
        if (company?.id)
        {
            const {payload}: any = await dispatch(deleteCompany(company.id))
            if (payload?.status === 0)
            {
                setShowModal(false)
                if (payload?.message === "Удалено") history.goBack()
                else dispatch(getCompany({id, controller}))
            }
        }
    }, [company, history, id, controller])

    const data = useMemo(() => objectGroups?.data?.map(i => {
        return {
            key: i.id,
            id: i.id,
            name: i.name,
            active: i.active,
            deleted: i.deleted,
        }
    }), [objectGroups])

    if (!company) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{company?.name}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <div>
                    <Button type="link" onClick={() => history.push(`/companyForm`, {company})}>Редактировать</Button>
                    <Button type="link" danger onClick={() => setShowModal(true)}>Удалить</Button>
                </div>
            </div>

            <Descriptions bordered column={{xs: 1, sm: 1, md: 2}}>
                <Descriptions.Item label="Название">{company?.name}</Descriptions.Item>
                <Descriptions.Item label="ID">{company?.id}</Descriptions.Item>
                <Descriptions.Item label="Создана">{moment(company?.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Обновлена">{moment(company?.updatedAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Статус">
                    <div className="description-item-status">
                        <Tag color={company.active ? 'green' : 'volcano'} style={{marginRight: 0}}>{company.active ? 'Активно' : 'Не активно'}</Tag>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Реквизиты">{company.requisites}</Descriptions.Item>
                <Descriptions.Item label="Комментарий">{company.comment}</Descriptions.Item>
            </Descriptions>

            <div className="tabled-side">
                <Row gutter={[24, 0]}>
                    <Col xs={24}>
                        <Card variant="borderless"
                              className="criclebox tablespace mb-24"
                              title={
                                  <div style={{display: 'flex', alignItems: 'center'}}>
                                      <div>Группы</div>
                                      <Button onClick={() => history.push('/objectGroupForm', {idCompany: id})} type="link" size="middle" style={{marginTop: 5}}>
                                          Добавить
                                      </Button>
                                  </div>
                              }
                              extra={
                                  <div className="table-title-extra">
                                      <FormInput placeholder="Поиск"
                                                 containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                 value={filter?.search}
                                                 name="search"
                                                 onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                                 allowClear
                                      />

                                      <FormSelect value={filter?.status}
                                                  containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                  dropdownStyle={{minWidth: 'min-content'}}
                                                  onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                                  options={isActiveOptionsSelect}
                                                  placeholder="Статус"
                                                  allowClear
                                                  placement="bottomRight"
                                      />

                                      <FormSelect value={filter?.limit}
                                                  containerStyle={{marginBottom: 0, width: 'auto'}}
                                                  dropdownStyle={{minWidth: 'min-content'}}
                                                  onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                                  options={limitOptionsSelect}
                                                  placement="bottomRight"
                                      />
                                  </div>
                              }
                        >
                            <div className="table-responsive">
                                {
                                    objectGroups
                                        ? <Table columns={columns}
                                                 dataSource={data}
                                                 pagination={filter?.limit ? {
                                                     position: objectGroups?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                     pageSize: filter.limit,
                                                     hideOnSinglePage: true,
                                                     total: objectGroups.total,
                                                     current: filter?.offset / filter?.limit + 1,
                                                     onChange: (page, pageSize) => {
                                                         const obj = {
                                                             limit: pageSize,
                                                             offset: (page - 1) * pageSize,
                                                         }
                                                         history.replace({search: filterToString({...filter, ...obj})})
                                                     },
                                                     showSizeChanger: false,
                                                 } : false}
                                                 className="ant-border-space"
                                                 onRow={({id}) => ({
                                                     onClick: () => history.push(`/objectGroup/${id}`),
                                                     onMouseDown: (e) => e.button === 1 && window.open(`/objectGroup/${id}`, '_blank', 'noopener,noreferrer')
                                                 })}
                                                 rowClassName={({deleted}) => deleted ? 'table-row-deleted' : ''}
                                        />
                                        : <Loading/>
                                }
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            <Modal title={`Вы уверены что хотите удалить компанию`}
                   centered
                   open={showModal}
                   onOk={deleteHandler}
                   onCancel={() => setShowModal(false)}
                   okText="Да"
                   okButtonProps={{loading: status === "loading"}}
            />
        </div>
    )
}